/**
 * Dashboard — the main call-coaching screen (formerly the bare CallList).
 *
 * Wired to REAL data:
 *   - KPI row      ← GET /api/calls/stats   (role-aware aggregates + deltas)
 *   - Call table   ← GET /api/calls          (enriched rows: self/AI/notes)
 *
 * Both endpoints are role-scoped server-side (staff see only their own calls;
 * managers/admins see the tenant) and gate-faithful: a staff member who hasn't
 * self-scored a call gets ai_review_score=null and manager_notes_count=0 on that
 * row, and those calls don't leak into the AI aggregates. We render those as a
 * "Pending"/locked placeholder — never a real number the gate would hide.
 *
 * ALL filters are server-side now (routes/calls.ts): direction, status,
 * extension/agent, from/to, free-text search, score band (scoreMin/scoreMax,
 * "Not scored" via scoreBand=unscored), self-score state, AI-review state, and
 * manager-notes presence ride as query params, and the gated ones are applied
 * by the server against the SAME gated values the rows carry. Pagination is
 * REAL server pagination (limit/offset; metadata on X-Total-Count/X-Has-More
 * headers via api.listCallsPaged) — no more 200-row client window.
 *
 * The extension dropdown is labeled with rep names for managers/admins (from
 * GET /api/manager/staff — staff can't call it, so they fall back to the raw
 * extension numbers seen in their own rows).
 *
 * The stats endpoint accepts the same filters EXCEPT scoreBand=unscored and
 * notes (it strips unknown params), so the KPI cards don't narrow on those two
 * — same as the old client-side behavior.
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { api, ApiRequestError } from "@/lib/api";
import { queryKeys } from "@/lib/queryKeys";
import type {
  CallDirection,
  CallListFilters,
  CallListPage,
  CallRow,
  CallStats,
  PipelineStatus,
  SavedView,
} from "@/types";
import { useAuth } from "@/auth/AuthProvider";
import { useSimulation } from "@/auth/SimulationProvider";
import { PageHeader, Card, Button, SelectField, ProgressBar } from "@/components/ui";
import { LoadingState } from "@/components/LoadingState";
import { EmptyState } from "@/components/EmptyState";
import { ErrorState } from "@/components/ErrorState";
import { scoreBand, scoreBandLabel, scoreBandClasses, secToClock } from "@/lib/format";
import {
  IconPhone,
  IconCheckCircle,
  IconTrendUp,
  IconSparkle,
  IconAlert,
  IconSearch,
  IconArrowDownLeft,
  IconArrowUpRight,
  IconNote,
} from "@/components/icons";

const PAGE_SIZE = 20;

// Pipeline statuses where a flagged call is still being processed.
const IN_FLIGHT = new Set<PipelineStatus>([
  "discovered",
  "recording_ready",
  "transcribing",
  "transcribed",
  "scoring",
]);

const PROCESSING_LABELS: Partial<Record<PipelineStatus, string>> = {
  discovered: "Queued",
  recording_ready: "Fetching audio",
  transcribing: "Transcribing",
  transcribed: "Scoring",
  scoring: "AI scoring",
};

// ── Filter option types (ALL server-backed now — see toServerFilters). ───────
type ScoreBandFilter = "" | "strong" | "solid" | "fair" | "weak" | "unscored";
type SelfScoreFilter = "" | "submitted" | "pending";
type AiReviewFilter = "" | "reviewed" | "scored" | "none";
type NotesFilter = "" | "has" | "none";

interface FilterState {
  direction: "" | CallDirection;
  status: "" | PipelineStatus;
  extension: string;
  from: string; // yyyy-mm-dd (local date input)
  to: string;
  search: string;
  scoreBand: ScoreBandFilter;
  selfScore: SelfScoreFilter;
  aiReview: AiReviewFilter;
  notes: NotesFilter;
  /** Quick toggle: only calls with a revealed AI score below the attention bar. */
  needsAttention: boolean;
}

const EMPTY_FILTERS: FilterState = {
  direction: "",
  status: "",
  extension: "",
  from: "",
  to: "",
  search: "",
  scoreBand: "",
  selfScore: "",
  aiReview: "",
  notes: "",
  needsAttention: false,
};

// A score at/under this is "needs attention" (mirrors the stats endpoint).
const ATTENTION_BELOW = 60;

// ── Saved views (CP3, per-user) ──────────────────────────────────────────────
// A saved view stores the Dashboard's FilterState as a flat scalar bag (only
// the non-default entries). Applying one re-validates every value against the
// allowed sets below — a stale/garbled value falls back to the default — and
// then flows through toServerFilters → GET /api/calls like any manual filter,
// so every server gate (role scoping, reveal gate, blocklist) is re-applied.

const DIRECTION_VALUES: ReadonlyArray<FilterState["direction"]> = [
  "",
  "Inbound",
  "Outbound",
];
const STATUS_VALUES: ReadonlyArray<FilterState["status"]> = [
  "",
  "discovered",
  "recording_ready",
  "transcribing",
  "transcribed",
  "scoring",
  "scored",
  "error",
  "skipped",
];
const SCORE_BAND_VALUES: ReadonlyArray<ScoreBandFilter> = [
  "",
  "strong",
  "solid",
  "fair",
  "weak",
  "unscored",
];
const SELF_SCORE_VALUES: ReadonlyArray<SelfScoreFilter> = ["", "submitted", "pending"];
const AI_REVIEW_VALUES: ReadonlyArray<AiReviewFilter> = ["", "reviewed", "scored", "none"];
const NOTES_VALUES: ReadonlyArray<NotesFilter> = ["", "has", "none"];

/** Validate a stored value against an allowed set; fall back to the default. */
function pickEnum<T extends string>(v: unknown, allowed: readonly T[], fallback: T): T {
  return typeof v === "string" && (allowed as readonly string[]).includes(v)
    ? (v as T)
    : fallback;
}
function pickString(v: unknown): string {
  return typeof v === "string" ? v : "";
}

/** The flat scalar bag a saved view stores (only non-default entries). */
function savedFiltersFromState(f: FilterState): Record<string, string | boolean> {
  const out: Record<string, string | boolean> = {};
  for (const k of Object.keys(EMPTY_FILTERS) as Array<keyof FilterState>) {
    if (f[k] !== EMPTY_FILTERS[k]) out[k] = f[k];
  }
  return out;
}

/** Rebuild a full FilterState from a stored bag, re-validating every value. */
function filterStateFromSaved(saved: Record<string, unknown>): FilterState {
  return {
    direction: pickEnum(saved.direction, DIRECTION_VALUES, ""),
    status: pickEnum(saved.status, STATUS_VALUES, ""),
    extension: pickString(saved.extension),
    from: pickString(saved.from),
    to: pickString(saved.to),
    search: pickString(saved.search),
    scoreBand: pickEnum(saved.scoreBand, SCORE_BAND_VALUES, ""),
    selfScore: pickEnum(saved.selfScore, SELF_SCORE_VALUES, ""),
    aiReview: pickEnum(saved.aiReview, AI_REVIEW_VALUES, ""),
    notes: pickEnum(saved.notes, NOTES_VALUES, ""),
    needsAttention: saved.needsAttention === true,
  };
}

// Quick date presets for the compact filter card → concrete from/to (local dates).
type DatePreset = "" | "today" | "7d" | "30d" | "month";
function presetRange(preset: DatePreset): { from: string; to: string } {
  if (!preset) return { from: "", to: "" };
  const fmt = (d: Date) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  const now = new Date();
  const today = fmt(now);
  if (preset === "today") return { from: today, to: today };
  if (preset === "month") return { from: fmt(new Date(now.getFullYear(), now.getMonth(), 1)), to: today };
  const back = preset === "7d" ? 6 : 29;
  const start = new Date(now);
  start.setDate(start.getDate() - back);
  return { from: fmt(start), to: today };
}

// Score-band select → scoreMin/scoreMax server params. Thresholds mirror
// lib/format scoreBand (85+ strong · 70–84 solid · 50–69 fair · <50 weak).
const BAND_RANGES: Record<
  Exclude<ScoreBandFilter, "" | "unscored">,
  { min?: number; max?: number }
> = {
  strong: { min: 85 },
  solid: { min: 70, max: 84 },
  fair: { min: 50, max: 69 },
  weak: { max: 49 },
};

/** Build the server query — every dashboard filter now maps to a server param. */
function toServerFilters(f: FilterState): CallListFilters {
  // Score band → a [min, max] range; the Needs-Attention toggle intersects it
  // with "< ATTENTION_BELOW" (scores are integers, so < 60 ⇔ ≤ 59). A
  // contradictory combination (e.g. strong + needs-attention) yields an empty
  // range and the server returns zero rows — same as the old client filter.
  let scoreMin: number | undefined;
  let scoreMax: number | undefined;
  if (f.scoreBand && f.scoreBand !== "unscored") {
    const band = BAND_RANGES[f.scoreBand];
    scoreMin = band.min;
    scoreMax = band.max;
  }
  if (f.needsAttention) {
    scoreMax = Math.min(scoreMax ?? ATTENTION_BELOW - 1, ATTENTION_BELOW - 1);
  }
  const search = f.search.trim();
  return {
    ...(f.direction ? { direction: f.direction } : {}),
    ...(f.status ? { status: f.status } : {}),
    ...(f.extension ? { extension: f.extension } : {}),
    ...(f.from ? { from: new Date(`${f.from}T00:00:00`).toISOString() } : {}),
    ...(f.to ? { to: new Date(`${f.to}T23:59:59.999`).toISOString() } : {}),
    ...(search ? { search } : {}),
    ...(f.selfScore ? { selfScore: f.selfScore } : {}),
    ...(f.aiReview ? { aiReview: f.aiReview } : {}),
    ...(f.scoreBand === "unscored" ? { scoreBand: "unscored" as const } : {}),
    ...(f.notes ? { notes: f.notes } : {}),
    ...(scoreMin !== undefined ? { scoreMin } : {}),
    ...(scoreMax !== undefined ? { scoreMax } : {}),
  };
}

// ── KPI card ────────────────────────────────────────────────────────────────
type KpiTone = "primary" | "success" | "warning" | "danger";

const KPI_ICON_CLASSES: Record<KpiTone, string> = {
  primary: "bg-primary/10 text-primary",
  success: "bg-band-solid/15 text-band-solid",
  warning: "bg-band-fair/15 text-band-fair",
  danger: "bg-band-weak/15 text-band-weak",
};

function KpiCard({
  icon,
  label,
  value,
  tone,
  topRight,
  valueClass,
  hint,
}: {
  icon: React.ReactNode;
  label: string;
  value: React.ReactNode;
  tone: KpiTone;
  /** Compact slot beside the icon (progress bar or delta) — keeps the card short. */
  topRight?: React.ReactNode;
  valueClass?: string;
  /** Small footnote under the value (e.g. "sales only; service shown separately"). */
  hint?: React.ReactNode;
}) {
  return (
    <Card className="flex flex-col gap-1.5 px-4 py-3">
      <div className="flex items-start justify-between gap-2">
        <span
          className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${KPI_ICON_CLASSES[tone]}`}
        >
          {icon}
        </span>
        {topRight && <div className="min-w-0 flex-1 pt-0.5 text-right">{topRight}</div>}
      </div>
      <div>
        <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
          {label}
        </p>
        <p className={`text-2xl font-semibold leading-tight tabular-nums ${valueClass ?? ""}`}>
          {value}
        </p>
        {hint && <p className="mt-0.5 text-[11px] leading-snug text-muted-foreground">{hint}</p>}
      </div>
    </Card>
  );
}

/** Compact "% of total" + progress bar for the top-right of a KPI card. */
function KpiProgress({ pct, tone }: { pct: number; tone: "success" | "warning" }) {
  return (
    <div className="flex flex-col items-end gap-1">
      <span className="text-[11px] text-muted-foreground">{pct}% of total</span>
      <ProgressBar value={pct} tone={tone} className="w-full max-w-[6.5rem]" />
    </div>
  );
}

/** Render a signed delta as a small trend chip (only when present). */
function DeltaChip({ delta, unit }: { delta: number; unit?: string }) {
  if (delta === 0) {
    return <span className="text-[11px] text-muted-foreground">no change</span>;
  }
  const up = delta > 0;
  return (
    <span className={`text-[11px] ${up ? "text-band-solid" : "text-band-weak"}`}>
      {up ? "▲" : "▼"} {Math.abs(delta)}
      {unit ?? ""} <span className="text-muted-foreground">vs prev</span>
    </span>
  );
}

/** The four KPI cards (Needs Attention moved to the quick-filter card). Returns
 *  a fragment so the caller places them in the shared KPI/quick-filter grid.
 *  For STAFF the labels say "Your …": stats count only calls the rep is the
 *  evaluated rep of, while the table below also lists covered calls on their
 *  line — the label keeps the two from reading as a contradiction. */
function KpiCards({ stats, isStaff }: { stats: CallStats; isStaff: boolean }) {
  const hasDeltas = "total_calls_delta" in stats;
  return (
    <>
      <KpiCard
        icon={<IconPhone className="h-4 w-4" />}
        tone="primary"
        label={isStaff ? "Your Calls" : "Total Calls"}
        value={stats.total_calls}
        topRight={
          hasDeltas && stats.total_calls_delta !== undefined ? (
            <DeltaChip delta={stats.total_calls_delta} />
          ) : undefined
        }
      />
      <KpiCard
        icon={<IconCheckCircle className="h-4 w-4" />}
        tone="success"
        label={isStaff ? "Your Scored Calls" : "Scored Calls"}
        value={stats.scored_calls}
        topRight={<KpiProgress pct={stats.scored_pct} tone="success" />}
      />
      <KpiCard
        icon={<IconTrendUp className="h-4 w-4" />}
        tone="primary"
        label="Avg. Sales Score"
        value={
          stats.avg_score === null ? (
            <span className="text-muted-foreground">—</span>
          ) : (
            <>
              {stats.avg_score}
              <span className="ml-1 text-base font-normal text-muted-foreground">/100</span>
            </>
          )
        }
        valueClass={stats.avg_score === null ? "" : scoreTextClass(stats.avg_score)}
        topRight={
          hasDeltas && stats.avg_score_delta !== undefined && stats.avg_score_delta !== null ? (
            <DeltaChip delta={stats.avg_score_delta} unit=" pts" />
          ) : undefined
        }
        hint={
          // Sales-only headline (D2 — never blended with service). Show the service
          // average separately when there are service calls.
          stats.avg_score_admin != null && (stats.admin_count ?? 0) > 0
            ? `Service avg ${stats.avg_score_admin}/100 (${stats.admin_count} call${stats.admin_count === 1 ? "" : "s"}), tracked separately`
            : stats.avg_score_note
        }
      />
      <KpiCard
        icon={<IconSparkle className="h-4 w-4" />}
        tone="warning"
        label="AI Reviewed"
        value={stats.ai_reviewed_count}
        topRight={<KpiProgress pct={stats.ai_reviewed_pct} tone="warning" />}
      />
    </>
  );
}

/** One labeled extension option ("Dustin (ext 202)" or the raw "Ext. 203"). */
interface ExtensionOption {
  value: string;
  label: string;
}

/** Compact quick-filter card occupying the old "Needs Attention" KPI slot:
 *  Date preset, Agent extension, a Needs-Attention toggle, and a button that
 *  expands the full filter panel below. Changes apply immediately. */
function QuickFilterCard({
  datePreset,
  extension,
  extensionOptions,
  needsAttention,
  needsAttentionCount,
  showMore,
  onDatePreset,
  onExtension,
  onToggleAttention,
  onToggleMore,
}: {
  datePreset: DatePreset;
  extension: string;
  extensionOptions: ExtensionOption[];
  needsAttention: boolean;
  needsAttentionCount: number;
  showMore: boolean;
  onDatePreset: (p: DatePreset) => void;
  onExtension: (ext: string) => void;
  onToggleAttention: () => void;
  onToggleMore: () => void;
}) {
  return (
    <Card className="flex flex-col gap-2 px-3 py-2.5">
      <div className="grid grid-cols-2 gap-2">
        <label className="flex flex-col gap-0.5 text-[11px]">
          <span className="font-medium text-muted-foreground">Date</span>
          <select
            value={datePreset}
            onChange={(e) => onDatePreset(e.target.value as DatePreset)}
            className="rounded-md border bg-background px-1.5 py-1 text-xs outline-none focus:ring-2 focus:ring-ring"
          >
            <option value="">All time</option>
            <option value="today">Today</option>
            <option value="7d">Last 7 days</option>
            <option value="30d">Last 30 days</option>
            <option value="month">This month</option>
          </select>
        </label>
        <label className="flex flex-col gap-0.5 text-[11px]">
          <span className="font-medium text-muted-foreground">Extension</span>
          <select
            value={extension}
            onChange={(e) => onExtension(e.target.value)}
            className="rounded-md border bg-background px-1.5 py-1 text-xs outline-none focus:ring-2 focus:ring-ring"
          >
            <option value="">All</option>
            {extensionOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </label>
      </div>
      <button
        type="button"
        onClick={onToggleAttention}
        aria-pressed={needsAttention}
        className={`flex items-center justify-between rounded-md border px-2 py-1 text-xs font-medium transition ${
          needsAttention
            ? "border-band-weak/40 bg-band-weak/10 text-band-weak"
            : "bg-background text-foreground hover:bg-accent/40"
        }`}
      >
        <span className="flex items-center gap-1.5">
          <IconAlert className="h-3.5 w-3.5" /> Needs Attention
        </span>
        <span className={needsAttention ? "font-semibold" : "text-muted-foreground"}>
          {needsAttentionCount}
        </span>
      </button>
      <Button variant="ghost" className="w-full justify-center py-1 text-xs" onClick={onToggleMore}>
        {showMore ? "Hide filters ▲" : "More filters ▾"}
      </Button>
    </Card>
  );
}

/**
 * SavedViewsControl — the subtle per-user saved-views UI (CP3): a small
 * dropdown that applies a saved filter set, a "Save view" button that names
 * the CURRENT applied filters, and a delete for the selected view. Lives in
 * the page header, right above the filter cards. Errors (duplicate name, the
 * per-user cap) surface as small friendly messages, never raw responses.
 */
function SavedViewsControl({
  applied,
  selectedId,
  onSelect,
  onApply,
}: {
  /** The ACTIVE filter set (what "Save view" snapshots). */
  applied: FilterState;
  selectedId: string | null;
  onSelect: (id: string | null) => void;
  onApply: (view: SavedView) => void;
}) {
  const queryClient = useQueryClient();
  const [naming, setNaming] = useState(false);
  const [name, setName] = useState("");
  const [message, setMessage] = useState<string | null>(null);

  const viewsQuery = useQuery({
    queryKey: queryKeys.views,
    queryFn: ({ signal }) => api.listSavedViews(signal),
  });

  const createMutation = useMutation({
    mutationFn: () =>
      api.createSavedView({
        name: name.trim(),
        filters: savedFiltersFromState(applied),
      }),
    onSuccess: (view) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.views });
      setNaming(false);
      setName("");
      setMessage(null);
      onSelect(view.id);
    },
    onError: (err) => {
      setMessage(
        err instanceof ApiRequestError && err.body?.message
          ? err.body.message
          : "Couldn't save this view. Please try again.",
      );
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.deleteSavedView(id),
    onSuccess: (_data, id) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.views });
      if (selectedId === id) onSelect(null);
      setMessage(null);
    },
    onError: () => setMessage("Couldn't delete the view. Please try again."),
  });

  const views = viewsQuery.data ?? [];

  return (
    <div className="flex flex-col items-end gap-1">
      <div className="flex flex-wrap items-center justify-end gap-1.5">
        <select
          value={selectedId ?? ""}
          onChange={(e) => {
            const view = views.find((v) => v.id === e.target.value);
            if (view) onApply(view);
            else onSelect(null);
          }}
          disabled={viewsQuery.isLoading}
          aria-label="Apply a saved view"
          className="max-w-[11rem] rounded-md border bg-background px-2 py-1 text-xs outline-none focus:ring-2 focus:ring-ring disabled:opacity-60"
        >
          <option value="">
            {viewsQuery.isLoading
              ? "Loading views…"
              : views.length === 0
                ? "No saved views"
                : "Saved views…"}
          </option>
          {views.map((v) => (
            <option key={v.id} value={v.id}>
              {v.name}
            </option>
          ))}
        </select>
        {selectedId && !naming && (
          <Button
            type="button"
            variant="ghost"
            className="px-2 py-1 text-xs"
            disabled={deleteMutation.isPending}
            onClick={() => deleteMutation.mutate(selectedId)}
            title="Delete the selected view"
          >
            {deleteMutation.isPending ? "Deleting…" : "Delete"}
          </Button>
        )}
        {naming ? (
          <form
            className="flex items-center gap-1.5"
            onSubmit={(e) => {
              e.preventDefault();
              if (name.trim() && !createMutation.isPending) createMutation.mutate();
            }}
          >
            <input
              autoFocus
              value={name}
              maxLength={60}
              onChange={(e) => setName(e.target.value)}
              placeholder="View name…"
              className="w-36 rounded-md border bg-background px-2 py-1 text-xs outline-none focus:ring-2 focus:ring-ring"
            />
            <Button
              type="submit"
              variant="primary"
              className="px-2 py-1 text-xs"
              disabled={!name.trim() || createMutation.isPending}
            >
              {createMutation.isPending ? "Saving…" : "Save"}
            </Button>
            <Button
              type="button"
              variant="ghost"
              className="px-2 py-1 text-xs"
              onClick={() => {
                setNaming(false);
                setMessage(null);
              }}
            >
              Cancel
            </Button>
          </form>
        ) : (
          <Button
            type="button"
            variant="ghost"
            className="px-2 py-1 text-xs"
            onClick={() => setNaming(true)}
            title="Save the current filters as a named view"
          >
            Save view
          </Button>
        )}
      </div>
      {viewsQuery.isError && (
        <p className="text-xs text-destructive">Couldn't load saved views.</p>
      )}
      {message && <p className="text-xs text-destructive">{message}</p>}
    </div>
  );
}

/** Bare text color for a 0–100 score (matches the band tokens). */
function scoreTextClass(total: number): string {
  switch (scoreBand(total)) {
    case "strong":
      return "text-band-strong";
    case "solid":
      return "text-band-solid";
    case "fair":
      return "text-band-fair";
    case "weak":
      return "text-band-weak";
  }
}

// ── Per-row score cell ────────────────────────────────────────────────────────
function ScoreCell({
  row,
  gated,
  covered,
}: {
  row: CallRow;
  gated: boolean;
  /** Staff viewer is the line owner but NOT the evaluated rep — the call's
   *  review belongs to the covering rep, so its score is never theirs to see. */
  covered?: boolean;
}) {
  const ai = row.ai_review_score;
  if (ai !== null) {
    return (
      <div className="flex flex-col">
        <span className={`text-lg font-bold tabular-nums ${scoreTextClass(ai)}`}>{ai}</span>
        <span className="text-xs text-muted-foreground">{scoreBandLabel(ai)}</span>
      </div>
    );
  }
  // No AI number. Distinguish "skipped" / not-scored from "gated pending".
  if (row.status === "skipped") {
    return <span className="text-sm text-muted-foreground">— Skipped</span>;
  }
  if (covered && row.status === "scored") {
    return (
      <span
        className="inline-flex items-center gap-1 text-xs text-muted-foreground"
        title={`Scored — visible to ${row.evaluated_rep_name ?? "the rep who handled it"} and managers`}
      >
        <span aria-hidden>✓</span> Completed
      </span>
    );
  }
  if (gated) {
    // Staff hasn't self-scored → the score is intentionally withheld.
    return (
      <span className="inline-flex items-center gap-1 text-xs text-muted-foreground" title="Submit your self-score to reveal the AI score">
        <span aria-hidden>🔒</span> Locked
      </span>
    );
  }
  return <span className="text-sm text-muted-foreground">— Not scored</span>;
}

/** Self-score sub-cell (a colored badge or Pending/—). */
function SelfScoreCell({ value, covered }: { value: number | null; covered?: boolean }) {
  if (value === null) {
    // A covered call is never the viewer's to self-score — don't show "Pending".
    if (covered) return <span className="text-xs text-muted-foreground">—</span>;
    return <span className="text-xs text-muted-foreground">Pending</span>;
  }
  return (
    <span
      className={`inline-flex rounded-full px-2 py-0.5 text-xs font-semibold ${scoreBandClasses(value)}`}
      // Edge: the viewer self-scored BEFORE the call was re-attributed to a
      // covering rep. The number is theirs, but the detail page now belongs to
      // the cover — explain instead of letting the badge dangle.
      title={
        covered
          ? "Your self-score, recorded before this call was re-attributed."
          : undefined
      }
    >
      {value}
    </span>
  );
}

/** AI-review sub-cell (badge, "Queued…", or —). */
function AiReviewCell({
  row,
  gated,
  covered,
}: {
  row: CallRow;
  gated: boolean;
  covered?: boolean;
}) {
  if (row.ai_review_score !== null) {
    return (
      <span
        className={`inline-flex rounded-full px-2 py-0.5 text-xs font-semibold ${scoreBandClasses(
          row.ai_review_score,
        )}`}
      >
        {row.ai_review_score}
      </span>
    );
  }
  if (covered && row.status === "scored") {
    return (
      <span
        className="text-xs text-muted-foreground"
        title={`Reviewed — visible to ${row.evaluated_rep_name ?? "the rep who handled it"} and managers`}
      >
        🔒
      </span>
    );
  }
  if (gated) return <span className="text-xs text-muted-foreground">🔒</span>;
  if (row.review_requested && IN_FLIGHT.has(row.status)) {
    return (
      <span className="inline-flex items-center gap-1 text-xs text-primary">
        <span
          aria-hidden
          className="inline-block h-2.5 w-2.5 animate-spin rounded-full border-2 border-primary/30 border-t-primary"
        />
        {PROCESSING_LABELS[row.status] ?? "Processing"}
      </span>
    );
  }
  if (row.review_requested) return <span className="text-xs text-muted-foreground">Queued</span>;
  return <span className="text-xs text-muted-foreground">—</span>;
}

/** Notes count sub-cell. */
function NotesCell({ count }: { count: number }) {
  if (count === 0) return <span className="text-xs text-muted-foreground">—</span>;
  return (
    <span className="inline-flex items-center gap-1 text-xs text-foreground">
      <IconNote className="h-3.5 w-3.5 text-muted-foreground" />
      {count}
    </span>
  );
}

function DirectionPill({ direction }: { direction: CallDirection }) {
  if (direction === "Inbound") {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-band-solid/15 px-2 py-0.5 text-xs font-medium text-band-solid">
        <IconArrowDownLeft className="h-3 w-3" /> Inbound
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary">
      <IconArrowUpRight className="h-3 w-3" /> Outbound
    </span>
  );
}

/** Subtle row tint by AI score band (only when a revealed score exists). */
function rowTint(row: CallRow): string {
  const ai = row.ai_review_score;
  if (ai === null) return "";
  switch (scoreBand(ai)) {
    case "strong":
    case "solid":
      return "bg-band-solid/[0.04]";
    case "fair":
      return "bg-band-fair/[0.05]";
    case "weak":
      return "bg-band-weak/[0.06]";
  }
}

export function Dashboard() {
  const { user } = useAuth();
  const { effectiveRole, simulatedUser } = useSimulation();
  const isStaff = effectiveRole === "staff";
  // Matches the server's effective identity (X-Act-As while simulating). Used to
  // mark rows the staff viewer is NOT the evaluated rep of ("covered" calls on
  // their line, which persist in the log but stay fully gated).
  const effectiveUserId = simulatedUser?.id ?? user?.id ?? null;
  const queryClient = useQueryClient();

  // `applied` is what's sent to the server / drives queries; `draft` is the live
  // form state, committed on Apply (mirrors the mockup's Apply Filters button).
  const [applied, setApplied] = useState<FilterState>(EMPTY_FILTERS);
  const [draft, setDraft] = useState<FilterState>(EMPTY_FILTERS);
  const [page, setPage] = useState(0);
  // The big filter panel is collapsed by default (expanded via "More filters").
  const [showFilters, setShowFilters] = useState(false);
  const [datePreset, setDatePreset] = useState<DatePreset>("");
  // The currently-applied saved view (CP3). Cleared whenever the user changes
  // any filter by hand, so the dropdown never claims a view that's been edited.
  const [selectedViewId, setSelectedViewId] = useState<string | null>(null);

  const serverFilters = useMemo(() => toServerFilters(applied), [applied]);
  // The list adds REAL pagination params on top; stats stay page-independent so
  // the KPI cards don't refetch on every page turn.
  const pagedFilters = useMemo<CallListFilters>(
    () => ({ ...serverFilters, limit: PAGE_SIZE, offset: page * PAGE_SIZE }),
    [serverFilters, page],
  );
  const listKey = queryKeys.calls.list(pagedFilters);

  const statsQuery = useQuery({
    queryKey: queryKeys.calls.stats(serverFilters),
    queryFn: ({ signal }) => api.getCallStats(serverFilters, signal),
  });

  const {
    data: pageData,
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey: listKey,
    queryFn: ({ signal }) => api.listCallsPaged(pagedFilters, signal),
    // Keep the previous page on screen while the next one loads (no table flash).
    placeholderData: keepPreviousData,
    refetchInterval: (query) => {
      const data = query.state.data as CallListPage | undefined;
      return data?.rows.some((c) => c.review_requested && IN_FLIGHT.has(c.status))
        ? 5000
        : false;
    },
  });

  const pageRows = pageData?.rows ?? [];
  const total = pageData?.total ?? 0;
  const scanTruncated = pageData?.scanTruncated ?? false;
  const hasMore = pageData?.hasMore ?? false;
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));

  // If the data shrank under us (e.g. a filter applied elsewhere), clamp the
  // page back into range so the user is never stranded on an empty page.
  useEffect(() => {
    if (pageData && page > 0 && pageData.rows.length === 0 && pageData.total > 0) {
      setPage(Math.max(0, Math.ceil(pageData.total / PAGE_SIZE) - 1));
    }
  }, [pageData, page]);

  // Whether ANY filter is active (drives the "no match" vs "no calls" empty state).
  const filtersActive = useMemo(
    () =>
      (Object.keys(EMPTY_FILTERS) as Array<keyof FilterState>).some(
        (k) => applied[k] !== EMPTY_FILTERS[k],
      ),
    [applied],
  );

  // Agent/extension dropdown: managers/admins get the staff roster (rep names);
  // staff can't call that endpoint, so their options come from their own loaded
  // rows (raw extension numbers). The active selection always stays listed.
  const staffQuery = useQuery({
    queryKey: queryKeys.manager.staff,
    queryFn: ({ signal }) => api.getManagerStaff(signal),
    enabled: !isStaff,
  });
  const extensionOptions = useMemo<ExtensionOption[]>(() => {
    const byExt = new Map<string, string>();
    for (const s of staffQuery.data ?? []) {
      if (s.extension_number) {
        byExt.set(s.extension_number, `${s.name} (ext ${s.extension_number})`);
      }
    }
    for (const c of pageRows) {
      if (c.extension_number && !byExt.has(c.extension_number)) {
        byExt.set(c.extension_number, `Ext. ${c.extension_number}`);
      }
    }
    if (applied.extension && !byExt.has(applied.extension)) {
      byExt.set(applied.extension, `Ext. ${applied.extension}`);
    }
    return [...byExt.entries()]
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => a.value.localeCompare(b.value, undefined, { numeric: true }));
  }, [staffQuery.data, pageRows, applied.extension]);

  const firstIdx = total === 0 || pageRows.length === 0 ? 0 : page * PAGE_SIZE + 1;
  const lastIdx = page * PAGE_SIZE + pageRows.length;

  function commit(): void {
    setApplied(draft);
    setPage(0);
    setSelectedViewId(null);
  }
  function clear(): void {
    setDraft(EMPTY_FILTERS);
    setApplied(EMPTY_FILTERS);
    setDatePreset("");
    setPage(0);
    setSelectedViewId(null);
  }
  // Quick filters (date preset / extension / needs-attention) apply immediately,
  // based on the ACTIVE set, and keep the draft in sync so the panel matches.
  function applyQuick(patch: Partial<FilterState>): void {
    const next = { ...applied, ...patch };
    setApplied(next);
    setDraft(next);
    setPage(0);
    setSelectedViewId(null);
  }
  function onDatePreset(p: DatePreset): void {
    setDatePreset(p);
    applyQuick(presetRange(p));
  }
  // Apply a saved view: re-validate the stored bag into a full FilterState and
  // commit it to BOTH applied + draft (so the expanded panel matches).
  function applyView(view: SavedView): void {
    const next = filterStateFromSaved(view.filters);
    setApplied(next);
    setDraft(next);
    setDatePreset("");
    setPage(0);
    setSelectedViewId(view.id);
  }

  const reviewMutation = useMutation({
    mutationFn: ({ callId, requested }: { callId: string; requested: boolean }) =>
      api.setReviewFlag(callId, requested),
    onMutate: async ({ callId, requested }) => {
      await queryClient.cancelQueries({ queryKey: listKey });
      const previous = queryClient.getQueryData<CallListPage>(listKey);
      queryClient.setQueryData<CallListPage>(listKey, (old) =>
        old
          ? {
              ...old,
              rows: old.rows.map((c) =>
                c.id === callId ? { ...c, review_requested: requested } : c,
              ),
            }
          : old,
      );
      return { previous };
    },
    onError: (_err, _vars, context) => {
      if (context?.previous) queryClient.setQueryData(listKey, context.previous);
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: listKey });
      void queryClient.invalidateQueries({ queryKey: queryKeys.calls.stats(serverFilters) });
    },
  });

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <PageHeader
        title="Dashboard"
        subtitle={isStaff ? "Your recorded calls and coaching." : "Team calls, scores, and coaching."}
        actions={
          <SavedViewsControl
            applied={applied}
            selectedId={selectedViewId}
            onSelect={setSelectedViewId}
            onApply={applyView}
          />
        }
      />

      <div className="flex min-h-0 flex-1 flex-col gap-3 p-5">
        {/* KPI + QUICK-FILTER ROW */}
        {statsQuery.isError && (
          <ErrorState error={statsQuery.error} onRetry={() => void statsQuery.refetch()} />
        )}
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-3 xl:grid-cols-5">
          {statsQuery.data ? (
            <KpiCards stats={statsQuery.data} isStaff={isStaff} />
          ) : (
            Array.from({ length: 4 }).map((_, i) => (
              <Card key={i} className="px-4 py-3">
                <div className="h-[60px] animate-pulse rounded bg-muted" />
              </Card>
            ))
          )}
          <QuickFilterCard
            datePreset={datePreset}
            extension={applied.extension}
            extensionOptions={extensionOptions}
            needsAttention={applied.needsAttention}
            needsAttentionCount={statsQuery.data?.needs_attention_count ?? 0}
            showMore={showFilters}
            onDatePreset={onDatePreset}
            onExtension={(ext) => applyQuick({ extension: ext })}
            onToggleAttention={() => applyQuick({ needsAttention: !applied.needsAttention })}
            onToggleMore={() => setShowFilters((v) => !v)}
          />
        </div>

        {/* EXPANDABLE FILTER PANEL (collapsed by default) */}
        {showFilters && (
        <Card className="flex flex-col gap-4">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
            {/* Search (server-side: name / phone / extension substring) */}
            <label className="flex flex-col gap-1 text-sm">
              <span className="font-medium">Search</span>
              <span className="relative">
                <IconSearch className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <input
                  value={draft.search}
                  onChange={(e) => setDraft({ ...draft, search: e.target.value })}
                  placeholder="Name, phone, ext…"
                  className="w-full rounded-md border bg-background py-1.5 pl-8 pr-3 text-sm outline-none focus:ring-2 focus:ring-ring"
                />
              </span>
            </label>

            {/* Date range */}
            <label className="flex flex-col gap-1 text-sm">
              <span className="font-medium">From</span>
              <input
                type="date"
                value={draft.from}
                max={draft.to || undefined}
                onChange={(e) => setDraft({ ...draft, from: e.target.value })}
                className="rounded-md border bg-background px-3 py-1.5 text-sm outline-none focus:ring-2 focus:ring-ring"
              />
            </label>
            <label className="flex flex-col gap-1 text-sm">
              <span className="font-medium">To</span>
              <input
                type="date"
                value={draft.to}
                min={draft.from || undefined}
                onChange={(e) => setDraft({ ...draft, to: e.target.value })}
                className="rounded-md border bg-background px-3 py-1.5 text-sm outline-none focus:ring-2 focus:ring-ring"
              />
            </label>

            <SelectField
              label="Direction"
              value={draft.direction}
              onChange={(e) => setDraft({ ...draft, direction: e.target.value as "" | CallDirection })}
              options={[
                { value: "", label: "All" },
                { value: "Inbound", label: "Inbound" },
                { value: "Outbound", label: "Outbound" },
              ]}
            />

            {/* Agent / extension (managers pick a rep by name; for staff it's
                their own extensions, derived from the loaded rows). */}
            <SelectField
              label="Agent (ext.)"
              value={draft.extension}
              onChange={(e) => setDraft({ ...draft, extension: e.target.value })}
              options={[{ value: "", label: "All" }, ...extensionOptions]}
            />

            <SelectField
              label="Score"
              value={draft.scoreBand}
              onChange={(e) => setDraft({ ...draft, scoreBand: e.target.value as ScoreBandFilter })}
              options={[
                { value: "", label: "All" },
                { value: "strong", label: "Excellent (85+)" },
                { value: "solid", label: "Great (70–84)" },
                { value: "fair", label: "Needs work (50–69)" },
                { value: "weak", label: "Poor (<50)" },
                { value: "unscored", label: "Not scored" },
              ]}
            />

            <SelectField
              label="Status"
              value={draft.status}
              onChange={(e) => setDraft({ ...draft, status: e.target.value as "" | PipelineStatus })}
              options={[
                { value: "", label: "All" },
                { value: "scored", label: "Scored" },
                { value: "transcribed", label: "Transcribed" },
                { value: "discovered", label: "Discovered" },
                { value: "skipped", label: "Skipped" },
                { value: "error", label: "Error" },
              ]}
            />

            <SelectField
              label="Self-Score"
              value={draft.selfScore}
              onChange={(e) => setDraft({ ...draft, selfScore: e.target.value as SelfScoreFilter })}
              options={[
                { value: "", label: "All" },
                { value: "submitted", label: "Submitted" },
                { value: "pending", label: "Pending" },
              ]}
            />

            <SelectField
              label="AI Review"
              value={draft.aiReview}
              onChange={(e) => setDraft({ ...draft, aiReview: e.target.value as AiReviewFilter })}
              options={[
                { value: "", label: "All" },
                { value: "scored", label: "Scored" },
                { value: "reviewed", label: "Flagged for review" },
                { value: "none", label: "Not flagged" },
              ]}
            />

            <SelectField
              label="Manager Notes"
              value={draft.notes}
              onChange={(e) => setDraft({ ...draft, notes: e.target.value as NotesFilter })}
              options={[
                { value: "", label: "All" },
                { value: "has", label: "Has notes" },
                { value: "none", label: "No notes" },
              ]}
            />
          </div>

          <div className="flex items-center justify-end gap-2">
            <Button variant="ghost" onClick={clear}>
              Clear Filters
            </Button>
            <Button variant="primary" onClick={commit}>
              Apply Filters
            </Button>
          </div>
        </Card>
        )}

        {/* CALL TABLE — fills the remaining height; scrolls internally so the
            page itself never scrolls. */}
        {isLoading && <LoadingState label="Loading calls…" />}
        {isError && <ErrorState error={error} onRetry={() => void refetch()} />}
        {!isLoading && !isError && total === 0 && !filtersActive && (
          <EmptyState
            title="No calls yet"
            description="Recorded calls appear here once ingestion is enabled. Each call gets a transcript, your self-score, and an AI coach review."
          />
        )}
        {!isLoading && !isError && total === 0 && filtersActive && (
          <EmptyState
            title="No calls match your filters"
            description="Try clearing or widening the filters above."
            action={
              <Button variant="ghost" onClick={clear}>
                Clear Filters
              </Button>
            }
          />
        )}
        {total > 0 && (
          <Card className="flex min-h-0 flex-1 flex-col overflow-hidden p-0">
            <div className="min-h-0 flex-1 overflow-auto">
              <table className="w-full min-w-[1040px] text-sm">
                <thead className="sticky top-0 z-10 border-b bg-muted text-left text-xs text-muted-foreground">
                  <tr>
                    <th className="px-4 py-2.5 font-medium">Date / Time</th>
                    <th className="px-4 py-2.5 font-medium">Direction</th>
                    <th className="px-4 py-2.5 font-medium">Agent / Ext.</th>
                    <th className="px-4 py-2.5 font-medium">Customer</th>
                    <th className="px-4 py-2.5 font-medium">Phone</th>
                    <th className="px-4 py-2.5 font-medium">Dur.</th>
                    <th className="px-4 py-2.5 font-medium">Score</th>
                    <th className="px-3 py-2.5 text-center font-medium" title="User notes">
                      Notes
                    </th>
                    <th className="px-3 py-2.5 text-center font-medium">Self</th>
                    <th className="px-3 py-2.5 text-center font-medium" title="Manager notes">
                      Mgr
                    </th>
                    <th className="px-3 py-2.5 text-center font-medium">AI</th>
                    <th className="px-3 py-2.5 text-center font-medium">Flag</th>
                    <th className="px-4 py-2.5 text-right font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {pageRows.map((c) => {
                    // A staff-visible row the viewer is NOT the evaluated rep of:
                    // covered by another rep on their line, or unattributed (no
                    // evaluated rep — null ext_owner snapshot). It persists in
                    // their log but is fully gated — never theirs to score/flag.
                    const covered =
                      isStaff &&
                      !(c.evaluated_rep_id != null && c.evaluated_rep_id === effectiveUserId);
                    // For a staff caller, a withheld AI score/manager notes means
                    // they haven't self-scored this call yet (the gate). Detect it
                    // so the cells show a lock rather than a bare dash. Covered
                    // rows are excluded — the viewer can never unlock those.
                    const gated =
                      isStaff &&
                      !covered &&
                      c.ai_review_score === null &&
                      c.status === "scored";
                    const customer =
                      (c.direction === "Inbound" ? c.from_name : c.to_name) ?? null;
                    const phone = c.direction === "Inbound" ? c.from_number : c.to_number;
                    return (
                      <tr
                        key={c.id}
                        className={`border-b last:border-0 hover:bg-accent/40 ${rowTint(c)}`}
                      >
                        <td className="px-4 py-2.5">
                          <Link className="font-medium hover:underline" to={`/calls/${c.id}`}>
                            {c.started_at ? new Date(c.started_at).toLocaleString() : "—"}
                          </Link>
                        </td>
                        <td className="px-4 py-2.5">
                          <DirectionPill direction={c.direction} />
                        </td>
                        <td className="px-4 py-2.5">
                          <span className="text-muted-foreground">Ext. {c.extension_number}</span>
                          {/* Attribution marker only when an actual cover exists —
                              an unattributed row has no rep to point at. */}
                          {covered && c.evaluated_rep_id != null && (
                            <span
                              className="mt-0.5 block text-xs text-primary"
                              title={`This call on your line was answered by ${c.evaluated_rep_name ?? "another rep"} — their review, not yours.`}
                            >
                              ↪ {c.evaluated_rep_name ?? "another rep"}
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-2.5">
                          {customer ? (
                            <span className="truncate">{customer}</span>
                          ) : (
                            <span className="text-muted-foreground">—</span>
                          )}
                        </td>
                        <td className="px-4 py-2.5">
                          {phone ? (
                            <span className="text-primary">{phone}</span>
                          ) : (
                            <span className="text-muted-foreground">—</span>
                          )}
                        </td>
                        <td className="px-4 py-2.5 tabular-nums text-muted-foreground">
                          {secToClock(c.duration_sec)}
                        </td>
                        <td className="px-4 py-2.5">
                          <ScoreCell row={c} gated={gated} covered={covered} />
                        </td>
                        <td className="px-3 py-2.5 text-center">
                          <NotesCell count={c.user_notes_count} />
                        </td>
                        <td className="px-3 py-2.5 text-center">
                          <SelfScoreCell value={c.self_score} covered={covered} />
                        </td>
                        <td className="px-3 py-2.5 text-center">
                          <NotesCell count={c.manager_notes_count} />
                        </td>
                        <td className="px-3 py-2.5 text-center">
                          <AiReviewCell row={c} gated={gated} covered={covered} />
                        </td>
                        <td className="px-3 py-2.5 text-center">
                          <input
                            type="checkbox"
                            className="h-4 w-4 cursor-pointer rounded border accent-primary disabled:cursor-not-allowed disabled:opacity-50"
                            aria-label="Flag this call for AI review"
                            checked={c.review_requested}
                            title={
                              covered
                                ? `Only ${c.evaluated_rep_name ?? "the rep who handled it"} or a manager can flag this call.`
                                : undefined
                            }
                            disabled={
                              covered ||
                              (reviewMutation.isPending &&
                                reviewMutation.variables?.callId === c.id)
                            }
                            onClick={(e) => e.stopPropagation()}
                            onChange={(e) => {
                              e.stopPropagation();
                              reviewMutation.mutate({ callId: c.id, requested: e.target.checked });
                            }}
                          />
                        </td>
                        <td className="px-4 py-2.5 text-right">
                          <Link
                            className="text-sm font-medium text-primary hover:underline"
                            to={`/calls/${c.id}`}
                          >
                            Review Call ›
                          </Link>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Pagination footer (server-side pages; total is a floor when the
                server hit its scan cap — shown as "N+"). */}
            <div className="flex shrink-0 flex-wrap items-center justify-between gap-3 border-t px-4 py-3 text-sm">
              <span className="text-muted-foreground">
                Showing {firstIdx} to {lastIdx} of {total}
                {scanTruncated ? "+" : ""} call{total === 1 && !scanTruncated ? "" : "s"}
              </span>
              <div className="flex items-center gap-1">
                <Button
                  variant="ghost"
                  className="px-2.5 py-1"
                  disabled={page === 0}
                  onClick={() => setPage((p) => Math.max(0, p - 1))}
                >
                  ‹ Prev
                </Button>
                <span className="px-2 text-muted-foreground">
                  Page {page + 1} of {pageCount}
                  {scanTruncated ? "+" : ""}
                </span>
                <Button
                  variant="ghost"
                  className="px-2.5 py-1"
                  disabled={!hasMore}
                  onClick={() => setPage((p) => p + 1)}
                >
                  Next ›
                </Button>
              </div>
            </div>
          </Card>
        )}
      </div>
    </div>
  );
}
